// app/javascript/projects/components/CodePageModal.jsx

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Modal } from 'ui';
import TranscriptPillLink from './TranscriptPillLink';
import Textarea from 'react-textarea-autosize';
import ColorConstants from 'constants/ColorConstants';
import EditableName from './EditableName';
import CloseModalButton from './CloseModalButton';
import ExcerptListComponent from './ExcerptListComponent';
import SortDropdown from './filters/SortDropdown';
import CodeModalFilterComponent from './filters/CodeModalFilterComponent';
import CodeableContextProvider from 'codeable/CodeableContextProvider';
import CodeOperationsProvider from 'codeable/CodeOperationsProvider';
import { useCodeDetails } from 'hooks/codes/queries';
import { useCodeExcerpts, useInvalidateCodeExcerpts } from 'hooks/excerpts/queries';
import { useToasts } from 'ui/toast';
import { useCodeReport } from 'hooks/code_reports/queries';
import { useAutoSaveSynthesis } from 'hooks/codes/useAutoSaveSynthesis';
import { useTranscriptCount } from 'hooks/transcripts/queries';
import { OP_DEFAULT } from 'constants/OpConstants';

const LEFT_PADDING = 45;

// Initial filter state factory
const getInitialFilters = () => ({
  codes: [],
  multi_choices: {},
  transcripts: [],
  surveys: [],
  op: OP_DEFAULT,
  sort: 'newest_activity'
});

const CodePageModal = (props) => {
  const textareaRef = useRef(null);
  const [sort, setSort] = useState('newest_activity');
  const [filters, setFilters] = useState(getInitialFilters);

  const { addToast } = useToasts();
  const { data: code, error: codeError, refetch } = useCodeDetails(props.codeID);
  const { data: codeReport, error: reportError, refetch: refetchCodeReport } = useCodeReport(props.codeID);
  const { data: transcriptCount = 0 } = useTranscriptCount(props.projectID);

  // Use the auto-save synthesis hook
  const {
    displayText,
    textAreaFocused,
    handleSynthesisChange,
    handleTextAreaFocus,
    handleTextAreaBlur
  } = useAutoSaveSynthesis(props.codeID, code?.synthesis);

  // Build the filters with sort
  const filtersWithSort = {
    ...filters,
    sort
  };

  // Fetch excerpts with filtering using useCodeExcerpts
  const {
    excerptIds,
    totalCount,
    isLoading: excerptsLoading,
    hasNextPage,
    loadMore,
    error: excerptsError
  } = useCodeExcerpts(props.codeID, filtersWithSort);

  // Get the invalidate function
  const invalidateCodeExcerpts = useInvalidateCodeExcerpts(props.codeID, filtersWithSort);

  // Handle filter changes
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  // refetch on codeID change and reset excerpts/filters
  useEffect(() => {
    if (!props.codeID) return;
    refetch();
    refetchCodeReport();
    setSort('newest_activity');
    setFilters(getInitialFilters());
  }, [props.codeID, refetch, refetchCodeReport]);

  useEffect(() => {
    if (codeError || excerptsError || reportError) {
      addToast('Something went wrong. Please refresh and try again.', { appearance: 'error' });
    }
  }, [codeError, excerptsError, reportError, addToast]);

  useEffect(() => {
    // refetch excerpts each time modal is opened for fresh data
    if (props.show) {
      invalidateCodeExcerpts();
    }
  }, [props.show])

  const onEditClick = useCallback((e) => {
    if (textAreaFocused) return;
    e.preventDefault();
    textareaRef.current?.focus();
  }, [textAreaFocused]);

  const onHide = useCallback((e) => {
    props.hideModal();
  }, [props]);

  const onLinkClick = useCallback((excerptID) => {
    props.hideModal();
  }, [props]);

  const onTranscriptClick = useCallback((e) => {
    props.hideModal();
  }, [props]);

  const getCodeDescription = useCallback(() => {
    const descriptionStyle = {
      width: "100%",
      resize: 'none',
      padding: '20px 30px',
      borderColor: "#d7d7d7",
      borderRadius: "5px",
      outline: "none",
      fontSize: '1.25em',
    };

    if (props.hasEditPermission) {
      return (
        <div style={{ marginTop: 40, width: "100%" }}>
          <Textarea 
            placeholder="Write a description or thoughts about this code"
            onChange={handleSynthesisChange}
            onFocus={handleTextAreaFocus}
            onBlur={handleTextAreaBlur}
            ref={textareaRef}
            value={displayText || ""}
            style={descriptionStyle}
          />
          <div
            onMouseDown={onEditClick}
            style={{
              textDecoration: 'none',
              float: 'right',
              cursor: 'pointer',
              fontSize: '.8em'
            }}
          >
            {textAreaFocused ? "Done" : "Edit"}
          </div>
          <div style={{ clear: 'both' }} />
        </div>
      );
    } else if (!!displayText) {
      return (
        <div style={{ marginTop: 40, width: "100%" }}>
          <div style={{
            ...descriptionStyle,
            borderStyle: 'solid',
            borderWidth: '1px'
          }}>
            {displayText}
          </div>
          <div style={{ clear: 'both' }} />
        </div>
      );
    } else {
      return null;
    }
  }, [textAreaFocused, displayText, props.hasEditPermission, handleSynthesisChange, handleTextAreaFocus, handleTextAreaBlur, onEditClick]);

  // Generate transcript pills from code report data
  const transcripts = codeReport?.report?.transcripts ? 
    Object.entries(codeReport.report.transcripts).map(([transcriptId, count]) => (
      <TranscriptPillLink
        key={transcriptId}
        id={parseInt(transcriptId)}
        count={count}
        onClick={onTranscriptClick}
      />
    )) : [];

  // Calculate number of transcripts this code appears in
  const transcriptAppearanceCount = codeReport?.report?.transcripts ? 
    Object.keys(codeReport.report.transcripts).length : 0;

  const userName = (props.user || {}).name || 'Unknown User';

  return (
    <Modal
      style={props.isModalOpen ? { filter: 'brightness(0.7)' } : {}}
      bsSize="large"
      show={props.show}
      onHide={onHide}
      enforceFocus={false}
    >
      <Modal.Body style={{ padding: 0, margin: 0, height: '90vh', overflow: 'auto' }}>
        <div style={{ padding: `30px ${LEFT_PADDING}px`, borderBottom: "1px solid lightGray" }}>
          <CloseModalButton onHide={onHide} />
          <h4>
            {props.hasEditPermission ?
              <EditableName
                title={code?.name}
                onSave={props.saveCodeName}
                blurredPostfix={code?.count ? ` (${code?.count})` : ''}
              /> : 
              <span style={{ fontSize: "1.5em" }}>
                {`${code?.name} (${code?.count})`}
              </span>
            }
          </h4>

          <div style={{ clear: 'both' }} />

          <div style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
          }}>
            <div style={{
              cursor: 'default',
              width: "50%"
            }}>
              Appears in {transcriptAppearanceCount}/{transcriptCount} transcripts
            </div>

            <div style={{
              cursor: 'default',
              width: "50%",
              textAlign: "right",
              color: ColorConstants.LIGHT_GREY
            }}>
              <em>Code added by {userName}</em>
            </div>
          </div>

          <div style={{ width: "100%", wordWrap: 'break-word' }}>
            {transcripts}
          </div>

          {getCodeDescription()}
        </div>
        <div style={{ padding: `10px ${LEFT_PADDING}px`, borderBottom: '1px solid lightGray', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <SortDropdown value={sort} onChange={setSort} />
          <CodeModalFilterComponent
            projectID={props.projectID}
            requiredCodeId={props.codeID}
            filters={filters}
            onFilterChange={handleFilterChange}
            onApplyFilter={invalidateCodeExcerpts}
            source="code_modal"
          />
        </div>
        <CodeableContextProvider
          projectID={props.projectID}
          includeAllUsers={true}
          location={props.location}
        >
          <CodeOperationsProvider
            projectId={props.projectID}
          >
            <ExcerptListComponent
              excerptIds={excerptIds || []}
              codePage={props.codePage}
              projectID={props.projectID}
              onLinkClick={onLinkClick}
              hasEditPermission={props.hasEditPermission}
              canPage={hasNextPage}
              showMore={loadMore}
              loading={excerptsLoading}
              headerText=""
            />
          </CodeOperationsProvider>
        </CodeableContextProvider>
      </Modal.Body>
    </Modal>
  );
};

export default CodePageModal;